import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import { useSidePanel } from "@rebasepro/cms";
import { useSnackbarController } from "@rebasepro/app";
import {
    Button,
    Card,
    Chip,
    cls,
    defaultBorderMixin,
    ExternalLinkIcon,
    iconSize,
    LucideIconByName,
    Typography
} from "@rebasepro/ui";
import { inboxPath } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";
import { EmailComposer } from "../inbox/EmailComposer";
import { isWaitingOnUs, sinceLabel, useMailboxes, whenInSentence, type Thread } from "../inbox/shared";
import { EntityEmailPanel } from "./EntityEmailPanel";
import { TasksPanel } from "./TasksPanel";
import { Timeline } from "./Timeline";

/**
 * A person, as the CRM sees them — the first tab their record opens on.
 *
 * The form that edits every field is one tab to the right; this is what an
 * operator opens a person to learn: who they are, what they said, where the
 * conversation stands, and the one thing to do next. The "Next up" card is
 * derived, never stored — a stage written down goes stale the moment somebody
 * replies, and this one cannot.
 *
 * Status changes go through the form context when there is one. Written to the
 * database behind the form's back, the form would put the old value back on
 * its next save.
 */

type Row = Record<string, unknown>;
type EnumOption = { id: string; label: string; color?: string };
type Email = { id: string; subject?: string | null; status?: string | null; created_at?: string | null; sent_at?: string | null };

const str = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const PIPELINE = ["found", "researching", "ready", "drafted", "contacted", "replied", "won", "lost", "not_a_fit", "do_not_contact"];

interface NextStep {
    chip: string;
    color: string;
    hint: string;
    action?: { label: string; run: () => void };
}

export function PersonOverview({ collection, entity, modifiedValues, formContext }: EntityCustomViewParams) {
    const navigate = useNavigate();
    const sidePanel = useSidePanel();
    const snackbar = useSnackbarController();
    const { mailboxes } = useMailboxes();
    const values = (modifiedValues ?? entity?.values ?? {}) as Row;
    const id = entity?.id != null ? String(entity.id) : "";

    const [threads, setThreads] = useState<Thread[]>([]);
    const [emails, setEmails] = useState<Email[]>([]);
    const [composing, setComposing] = useState(false);
    const [replyTo, setReplyTo] = useState<Thread | null>(null);

    // The status written from here when there is no live form to write it
    // through — see `setStatus`.
    const [statusWritten, setStatusWritten] = useState<string | undefined>();

    const email = str(values.email);
    const name = str(values.display_name) || str(values.handle) || "this person";
    const status = statusWritten ?? (str(values.status) || "found");
    const statusEnum = ((collection.properties?.status as { enum?: EnumOption[] } | undefined)?.enum ?? []);
    const statusOption = statusEnum.find(o => o.id === status);

    useEffect(() => {
        if (!id) return;
        let live = true;
        (async () => {
            const found = new Map<string, Thread>();
            const queries = [{ prospectId: ["==", id] }, ...(email ? [{ participants: ["array-contains", email.toLowerCase()] }] : [])];
            for (const where of queries) {
                const { data } = await client.data.collection<Thread>("email_threads").find({ where: where as never, orderBy: ["last_message_at", "desc"], limit: 20 });
                for (const t of data ?? []) found.set(t.id, t);
            }
            const { data: drafts } = await client.data.collection<Email>("emails").find({ where: { prospectId: ["==", id] } as never, orderBy: ["created_at", "desc"], limit: 10 });
            if (!live) return;
            setThreads([...found.values()].sort((a, b) => String(b.last_message_at ?? "").localeCompare(String(a.last_message_at ?? ""))));
            setEmails(drafts ?? []);
        })().catch(() => { /* the cover still shows who they are */ });
        return () => { live = false; };
    }, [id, email]);

    const setStatus = async (next: string) => {
        // Through the record form when this tab has a live one, so its Save and
        // unsaved-changes guard stay right. The admin can also hand a tab its
        // read-only stand-in — before the record form has mounted, or where a
        // screen replaces the form — whose `setFieldValue` and `save` throw. So
        // whether it is live is asked, not assumed.
        if (formContext && !formContext.readOnly && !formContext.disabled) {
            formContext.setFieldValue("status", next);
            formContext.save({ ...formContext.values, status: next } as never);
        } else {
            try {
                await client.data.collection("prospects").update(id, { status: next });
                setStatusWritten(next);
            } catch (err) {
                snackbar.open({ type: "error", message: `The status was not changed: ${err instanceof Error ? err.message : String(err)}` });
                return;
            }
        }
        void client.data.collection("activities").create({
            prospect: id,
            kind: "status_changed",
            summary: `Status: ${statusEnum.find(o => o.id === status)?.label ?? status} → ${statusEnum.find(o => o.id === next)?.label ?? next}`,
            actor: "human"
        }).catch(() => {});
        snackbar.open({ type: "success", message: `${name} is now “${statusEnum.find(o => o.id === next)?.label ?? next}”` });
    };

    const next: NextStep = useMemo(() => {
        const waiting = threads.find(isWaitingOnUs);
        const draft = emails.find(e => e.status === "needs_review" || e.status === "approved");
        const lastOut = threads.map(t => t.last_outbound_at).filter(Boolean).sort().at(-1);
        const lastIn = threads.map(t => t.last_inbound_at).filter(Boolean).sort().at(-1);
        if (status === "do_not_contact") return { chip: "Do not contact", color: "redLighter", hint: "They asked not to hear from us. Nothing is sent to them." };
        if (waiting) {
            return {
                chip: "Answer them", color: "orangeLighter",
                hint: `They wrote ${whenInSentence(waiting.last_inbound_at)}${waiting.last_snippet ? `: “${waiting.last_snippet}”` : "."}`,
                action: { label: "Open the conversation", run: () => navigate(inboxPath({ thread: waiting.id })) }
            };
        }
        if (draft) {
            return {
                chip: draft.status === "approved" ? "Send the approved email" : "Review the draft", color: "yellowLighter",
                hint: `“${draft.subject ?? "(no subject)"}” is waiting.`,
                action: { label: "Open the email", run: () => sidePanel.open({ path: "emails", entityId: draft.id }) }
            };
        }
        if (status === "won") return { chip: "Customer", color: "greenLighter", hint: "Won. Keep the timeline going." };
        if (status === "lost" || status === "not_a_fit") return { chip: "Closed", color: "grayLighter", hint: `Marked ${statusOption?.label?.toLowerCase() ?? status}.` };
        if (!email) {
            return { chip: "Find an email", color: "grayLighter", hint: str(values.no_email_reason) || "There is no address on file. Add one in the Details tab and they can be written to." };
        }
        if (!lastOut) {
            return { chip: "Write the first email", color: "blueLighter", hint: "Nobody has written to them yet.", action: { label: `Write to ${name.split(" ")[0]}`, run: () => setComposing(true) } };
        }
        const quietDays = lastOut ? Math.floor((Date.now() - Date.parse(lastOut)) / 86_400_000) : 0;
        if ((!lastIn || lastIn < lastOut) && quietDays >= 5) {
            const thread = threads.find(t => t.last_outbound_at === lastOut) ?? null;
            return {
                chip: "Follow up", color: "orangeLighter", hint: `We wrote ${quietDays} days ago and have not heard back.`,
                action: { label: "Write a follow-up", run: () => { setReplyTo(thread); setComposing(true); } }
            };
        }
        return { chip: "In conversation", color: "tealLighter", hint: lastIn ? `They last wrote ${whenInSentence(lastIn)}.` : `We last wrote ${whenInSentence(lastOut)}.` };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threads, emails, status, email, name]);

    if (!id) {
        return <Typography variant="body2" color="secondary" className="p-6">Save the person first — then their conversations, tasks and timeline live here.</Typography>;
    }

    return (
        <div className="flex flex-col gap-6 p-6 max-w-4xl">
            {/* Who they are */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <Typography variant="h5" className="break-words">{name}</Typography>
                    <Chip size="small" colorScheme={`${statusOption?.color ?? "gray"}Lighter` as never}>{statusOption?.label ?? status}</Chip>
                </div>
                {(str(values.role) || str(values.company)) && (
                    <Typography variant="body1" color="secondary">{[str(values.role), str(values.company)].filter(Boolean).join(" at ")}</Typography>
                )}
                <div className="flex items-center gap-1 flex-wrap -ml-2">
                    {email && (
                        <Button size="small" variant="text" component="a" href={`mailto:${email}`}>
                            <LucideIconByName name="Mail" size={iconSize.smallest} /> {email}
                        </Button>
                    )}
                    {str(values.profile_url) && (
                        <Button size="small" variant="text" component="a" href={str(values.profile_url)} target="_blank" rel="noreferrer">
                            <ExternalLinkIcon size={iconSize.smallest} /> {str(values.handle) || "Profile"}
                        </Button>
                    )}
                    {str(values.company_url) && (
                        <Button size="small" variant="text" component="a" href={str(values.company_url)} target="_blank" rel="noreferrer">
                            <LucideIconByName name="Globe" size={iconSize.smallest} /> Company site
                        </Button>
                    )}
                    {str(values.location) && <Typography variant="caption" color="secondary" className="ml-2">{str(values.location)}</Typography>}
                </div>
            </div>

            {/* Next up */}
            <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                <div className="flex items-center gap-2">
                    <Typography variant="caption" color="secondary">NEXT UP</Typography>
                    <Chip size="small" colorScheme={next.color as never}>{next.chip}</Chip>
                </div>
                <Typography variant="body2">{next.hint}</Typography>
                <div className="flex gap-2 flex-wrap">
                    {next.action && <Button size="small" variant="filled" color="primary" onClick={next.action.run}>{next.action.label}</Button>}
                    {email && status !== "do_not_contact" && !composing && next.action?.label !== `Write to ${name.split(" ")[0]}` && (
                        <Button size="small" variant="outlined" onClick={() => { setReplyTo(null); setComposing(true); }}>Write to them</Button>
                    )}
                </div>
            </Card>

            {composing && (
                <EmailComposer
                    target={replyTo
                        ? { mode: "reply", threadId: replyTo.id, mailboxId: replyTo.mailboxId, to: (replyTo.participants ?? []).filter(Boolean), prospectId: id, recipientName: name }
                        : { mode: "new", to: email ? [email] : [], subject: "", prospectId: id, recipientName: name, companyName: str(values.company) }}
                    mailboxes={mailboxes}
                    onCancel={() => { setComposing(false); setReplyTo(null); }}
                    onSent={async ({ threadId }) => {
                        setComposing(false);
                        setReplyTo(null);
                        snackbar.open({ type: "success", message: "Sent" });
                        navigate(inboxPath({ thread: threadId }));
                    }}
                />
            )}

            {/* What they said */}
            {(str(values.pain) || str(values.evidence)) && (
                <div className="flex flex-col gap-2">
                    <Typography variant="subtitle2">What they said</Typography>
                    {str(values.pain) && <Typography variant="body2">{str(values.pain)}</Typography>}
                    {str(values.evidence) && (
                        <blockquote className="border-l-2 border-surface-300 dark:border-surface-600 pl-3">
                            <Typography variant="body2" color="secondary" className="whitespace-pre-wrap italic">“{str(values.evidence)}”</Typography>
                        </blockquote>
                    )}
                </div>
            )}

            <EntityEmailPanel kind="person" entityId={id} email={email || null} name={name} companyName={str(values.company)} limit={3} showCompose={false} />

            <TasksPanel prospectId={id} />

            {emails.length > 0 && (
                <div className="flex flex-col gap-2">
                    <Typography variant="subtitle2">Outreach</Typography>
                    {emails.map(e => (
                        <button
                            key={e.id}
                            type="button"
                            onClick={() => sidePanel.open({ path: "emails", entityId: e.id })}
                            className={cls("text-left flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-surface-accent-50 dark:hover:bg-surface-accent-900", defaultBorderMixin)}
                        >
                            <Typography variant="body2" className="grow truncate" component="span">{e.subject ?? "(no subject)"}</Typography>
                            <Chip size="small" colorScheme={e.status === "sent" ? "greenLighter" : e.status === "needs_review" ? "yellowLighter" : e.status === "failed" ? "redLighter" : "grayLighter"}>{e.status}</Chip>
                            <Typography variant="caption" color="secondary" component="span">{sinceLabel(e.sent_at ?? e.created_at)}</Typography>
                        </button>
                    ))}
                </div>
            )}

            <Timeline prospectId={id} />

            {/* Where they are */}
            <div className="flex flex-col gap-2">
                <Typography variant="subtitle2">Where they are</Typography>
                <div className="flex gap-1.5 flex-wrap">
                    {PIPELINE.map(key => statusEnum.find(o => o.id === key)).filter((o): o is EnumOption => Boolean(o)).map(option => (
                        <Button
                            key={option.id}
                            size="small"
                            variant={option.id === status ? "filled" : "outlined"}
                            color={option.id === status ? "primary" : undefined}
                            aria-pressed={option.id === status}
                            onClick={() => { if (option.id !== status) void setStatus(option.id); }}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            </div>
        </div>
    );
}
